import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import { GatewayTokenRestoreError, getGatewayIdToken } from "./gatewaySession";

const giftTableApiBaseUrl = import.meta.env.VITE_GIFT_TABLE_API;

const giftTableApi = axios.create({
  baseURL: giftTableApiBaseUrl,
});

export class GiftTableApiError extends Error {
  constructor(
    message: string,
    readonly httpStatus?: number,
  ) {
    super(message);
    this.name = "GiftTableApiError";
  }
}

const getHttpStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

giftTableApi.interceptors.request.use(async (config) => {
  const token = getCookie("authToken");
  const publicRead = config.method === "get" && [
    "/api/gift-table", "/api/gift-table/search", "/api/gift-table/status", "/api/gift-table/slug",
  ].includes(config.url ?? "");
  if (token && !publicRead) {
    config.headers.Authorization = `Bearer ${await getGatewayIdToken()}`;
  }
  return config;
});

export interface Organizer {
  first_name: string;
  last_name: string;
}

export interface APIGiftTable {
  id?: string;
  user_id?: string;
  amphitryon: string;
  event_name: string;
  event_type: string;
  event_date: string;
  event_location: APIEventLocation;
  google_maps_link: string;
  event_place: string;
  message_to_guests: string;
  organizers?: Organizer[];
  theme: string;
  profile_picture: string;
  thumbnail_small: string;
  thumbnail_med: string;
  thumbnail_lg: string;
  status: string;
  plan: string;
  due_date: string;
  closed: boolean;
  closed_at: string;
  products: APIGiftTableProduct[];
  total_value: number;
  retention_fee_rate?: number;
  slug?: string;
}

export interface APIEventLocation {
  address: string;
  lat: number;
  lng: number;
}

export interface APIGiftTableProduct {
  product_id: string;
  category: string;
  category_id: string;
  brand: string;
  quantity: number;
  taken: number;
  priority: boolean;
  value: number;
  is_physical: boolean;
}

export interface APIGiftTableSearchResult {
  id: string;
  event_name: string;
  thumbnail_small: string;
  thumbnail_med: string;
  thumbnail_lg: string;
  event_type: string;
  event_date: string;
}

export const createGiftTable = async (
  giftTable: Partial<APIGiftTable>
): Promise<string> => {
  try {
    const response = await giftTableApi.post("/api/gift-table", giftTable);
    return response.data.id;
  } catch (error) {
    console.error("Error creating gift table", error);
    throw new Error("Failed to create gift table");
  }
};

export const updateGiftTable = async (
  giftTable: Partial<APIGiftTable>
): Promise<void> => {
  try {
    await giftTableApi.put("/api/gift-table", giftTable);
  } catch (error) {
    if (error instanceof GatewayTokenRestoreError) throw error;
    throw new GiftTableApiError("Failed to update gift table", getHttpStatus(error));
  }
};

export const getGiftTable = async (id: string): Promise<APIGiftTable> => {
  try {
    const response = await giftTableApi.get<APIGiftTable>("/api/gift-table", {
      params: { id },
    });
    const giftTable = response.data;
    return giftTable;
  } catch (error) {
    if (error instanceof GatewayTokenRestoreError) throw error;
    throw new GiftTableApiError("Failed to fetch gift table", getHttpStatus(error));
  }
};

export const getActiveGiftTableByUserID = async (
  userID: string
): Promise<string | null> => {
  try {
    const response = await giftTableApi.get<{ id: string }>(
      "/api/gift-table/active",
      {
        params: { user_id: userID },
      }
    );
    return response.data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    console.error("Error fetching active gift table", error);
    throw new Error("Failed to fetch active gift table");
  }
};

export const getGiftTableStatus = async (id: string): Promise<string> => {
  try {
    const response = await giftTableApi.get<{ status: string }>(
      "/api/gift-table/status",
      {
        params: { id },
      }
    );
    return response.data.status;
  } catch (error) {
    console.error("Error fetching gift table status", error);
    throw new Error("Failed to fetch gift table status");
  }
};

export const publishGiftTable = async (id: string): Promise<void> => {
  try {
    await giftTableApi.post("/api/gift-table/publish", null, {
      params: { id },
    });
  } catch (error) {
    console.error("Error publishing gift table", error);
    throw new Error("Failed to publish gift table");
  }
};

export const choosePlan = async (id: string, plan: string): Promise<void> => {
  try {
    await giftTableApi.post("/api/gift-table/my-table/choose-plan", null, {
      params: { id, plan },
    });
  } catch (error) {
    console.error("Error choosing plan", error);
    throw new Error("Failed to choose plan");
  }
};

export const checkGiftTableSlug = async (
  slug: string,
  excludeId?: string
): Promise<{ available: boolean; slug: string }> => {
  try {
    const response = await giftTableApi.get("/api/gift-table/slug", {
      params: {
        slug,
        ...(excludeId ? { exclude_id: excludeId } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      throw new Error("INVALID_SLUG");
    }
    console.error("Error validating gift table slug", error);
    throw new Error("Failed to validate gift table slug");
  }
};

export const searchGiftTables = async (
  eventName?: string,
  eventType?: string,
  eventDate?: string,
  startDate?: string,
  endDate?: string,
  limit: number = 10,
  startAfterID?: string
): Promise<APIGiftTableSearchResult[]> => {
  try {
    const response = await giftTableApi.get("/api/gift-table/search", {
      params: {
        event_name: eventName,
        event_type: eventType,
        event_date: eventDate,
        start_date: startDate,
        end_date: endDate,
        limit,
        start_after_id: startAfterID,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error searching gift tables", error);
    throw new Error("Failed to search gift tables");
  }
};

export const withdrawProduct = async (
  giftTableID: string,
  productID: string,
  quantity: number
): Promise<void> => {
  try {
    await giftTableApi.post("/api/gift-table/withdraw-product", {
      gift_table_id: giftTableID,
      product_id: productID,
      quantity,
    });
  } catch (error) {
    console.error("Error withdrawing product", error);
    throw new Error("Failed to withdraw product");
  }
};

export const refreshGiftTableCalculations = async (
  id: string
): Promise<void> => {
  try {
    await giftTableApi.post("/api/gift-table/refresh-calculations", null, {
      params: { id },
    });
  } catch (error) {
    console.error("Error refreshing gift table calculations", error);
    throw new Error("Failed to refresh gift table calculations");
  }
};
